import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType, PDfPaymentGrid } from '@makemydeal/dr-platform-types';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

export interface ScopeGridSelection {
    selectedTermIndices: number[];
    selectedDownPaymentIndices: number[];
}

export interface ScopeGridInfo {
    scopeId: string;
    hasGrid: boolean;
    offerType: OfferType;
    isFlexBuy: boolean;
    gridData: PDfPaymentGrid | undefined;
    selection: ScopeGridSelection;
}

export interface PrintGridSelectionState {
    scopeGridSelections: Record<string, ScopeGridSelection>;
    scopeGridInfos: ScopeGridInfo[];
    toggleTermIndex: (scopeId: string, index: number) => void;
    toggleDownPaymentIndex: (scopeId: string, index: number) => void;
    setSelectionForScope: (scopeId: string, selection: ScopeGridSelection) => void;
    resetSelectionForScope: (scopeId: string) => void;
    resetAllSelections: () => void;
    getSelectionForPayload: (scopeId: string) => ScopeGridSelection | undefined;
}

const getAllIndices = (length: number): number[] => Array.from({ length }, (_, i) => i);

const buildDefaultSelection = (gridData: PDfPaymentGrid | undefined): ScopeGridSelection => ({
    selectedTermIndices: getAllIndices(gridData?.termsOptions?.length ?? 3),
    selectedDownPaymentIndices: getAllIndices(gridData?.downPaymentOptions?.length ?? 3)
});

export const usePrintGridSelection = (selectedScopeIds: string[]): PrintGridSelectionState => {
    const scopeGridDataMap = useSelector(
        (realState: any) => {
            const activeScopesOrder = scopedStateSelectors.getActiveScopesOrder(realState);
            const result: Record<
                string,
                { hasGrid: boolean; offerType: OfferType; isFlexBuy: boolean; gridData: PDfPaymentGrid | undefined }
            > = {};

            for (const scopeId of activeScopesOrder) {
                const scopeState = scopedStateSelectors.getScopeStateById(realState, scopeId) as StateTree;
                if (!scopeState) continue;

                const offerType = offerReduxSelectors.getCurrentOfferType(scopeState);
                const isFlexBuy = offerReduxSelectors.isFordFlexBuyDeal(scopeState);
                const flexBuyGridToggle = offerReduxSelectors.getFlexBuyPaymentGridToggle(scopeState);
                const paymentGridToggle = offerReduxSelectors.getPaymentGridToggle(scopeState, offerType);
                const hasGrid = (isFlexBuy && flexBuyGridToggle) || (!isFlexBuy && paymentGridToggle);

                result[scopeId] = {
                    hasGrid,
                    offerType,
                    isFlexBuy,
                    gridData: hasGrid ? offerReduxSelectors.getPaymentGridABPencilPrinting(scopeState) : undefined
                };
            }

            return result;
        },
        /* istanbul ignore next -- useSelector equality function; exercised internally by react-redux */
        (prev, next) => {
            const prevKeys = Object.keys(prev);
            const nextKeys = Object.keys(next);
            if (prevKeys.length !== nextKeys.length) return false;
            return prevKeys.every((k) => {
                const p = prev[k];
                const n = next[k];
                if (!n) return false;
                return (
                    p.hasGrid === n.hasGrid &&
                    p.offerType === n.offerType &&
                    p.isFlexBuy === n.isFlexBuy &&
                    p.gridData === n.gridData
                );
            });
        }
    );

    const buildDefaultSelections = useCallback((): Record<string, ScopeGridSelection> => {
        const selections: Record<string, ScopeGridSelection> = {};
        for (const scopeId of Object.keys(scopeGridDataMap)) {
            /* istanbul ignore next -- defensive optional chain */
            selections[scopeId] = buildDefaultSelection(scopeGridDataMap[scopeId]?.gridData);
        }
        return selections;
    }, [scopeGridDataMap]);

    /**
     * `buildDefaultSelections` changes identity whenever `scopeGridDataMap` does, and that map is rebuilt on
     * every render once more than one scope is active: `getPaymentGridABPencilPrinting` is a `createSelector`
     * with a cache of one, so iterating scopes evicts each previous result and the equality check below
     * compares `gridData` by reference. Holding the builder in a ref keeps `resetAllSelections` stable, so
     * consumers can depend on it without re-running their effects on every render — `PrintPreviewSlideOut`
     * has it in an effect that calls it, which otherwise loops until React's update-depth guard trips.
     */
    const buildDefaultSelectionsRef = useRef(buildDefaultSelections);
    useEffect(() => {
        buildDefaultSelectionsRef.current = buildDefaultSelections;
    }, [buildDefaultSelections]);

    const [scopeGridSelections, setScopeGridSelections] = useState<Record<string, ScopeGridSelection>>(buildDefaultSelections);

    const prevScopeGridDataMapRef = useRef(scopeGridDataMap);
    useEffect(() => {
        const prev = prevScopeGridDataMapRef.current;
        const currentKeys = Object.keys(scopeGridDataMap);
        const prevKeys = Object.keys(prev);

        const hasNewScopes = currentKeys.some((k) => !prev[k]);
        const hasRemovedScopes = prevKeys.some((k) => !scopeGridDataMap[k]);
        const scopesWithGridSizeChanges = new Set(
            currentKeys.filter((k) => {
                if (!prev[k]) return false;
                const prevGrid = prev[k].gridData;
                /* istanbul ignore next -- defensive optional chain */
                const currGrid = scopeGridDataMap[k]?.gridData;
                return (
                    prevGrid?.termsOptions?.length !== currGrid?.termsOptions?.length ||
                    prevGrid?.downPaymentOptions?.length !== currGrid?.downPaymentOptions?.length
                );
            })
        );

        if (hasNewScopes || hasRemovedScopes || scopesWithGridSizeChanges.size > 0) {
            setScopeGridSelections((existing) => {
                const next: Record<string, ScopeGridSelection> = {};
                for (const scopeId of currentKeys) {
                    if (existing[scopeId] && !scopesWithGridSizeChanges.has(scopeId)) {
                        next[scopeId] = existing[scopeId];
                    } else {
                        /* istanbul ignore next -- defensive optional chain */
                        next[scopeId] = buildDefaultSelection(scopeGridDataMap[scopeId]?.gridData);
                    }
                }
                return next;
            });
        }

        prevScopeGridDataMapRef.current = scopeGridDataMap;
    }, [scopeGridDataMap]);

    const toggleTermIndex = useCallback((scopeId: string, index: number) => {
        setScopeGridSelections((prev) => {
            const current = prev[scopeId];
            if (!current) return prev;
            const indices = current.selectedTermIndices;
            if (indices.includes(index) && indices.length <= 1) return prev;
            const newIndices = indices.includes(index)
                ? indices.filter((i) => i !== index)
                : [...indices, index].sort((a, b) => a - b);
            return { ...prev, [scopeId]: { ...current, selectedTermIndices: newIndices } };
        });
    }, []);

    const toggleDownPaymentIndex = useCallback((scopeId: string, index: number) => {
        setScopeGridSelections((prev) => {
            const current = prev[scopeId];
            if (!current) return prev;
            const indices = current.selectedDownPaymentIndices;
            if (indices.includes(index) && indices.length <= 1) return prev;
            const newIndices = indices.includes(index)
                ? indices.filter((i) => i !== index)
                : [...indices, index].sort((a, b) => a - b);
            return { ...prev, [scopeId]: { ...current, selectedDownPaymentIndices: newIndices } };
        });
    }, []);

    const setSelectionForScope = useCallback((scopeId: string, selection: ScopeGridSelection) => {
        setScopeGridSelections((prev) => ({ ...prev, [scopeId]: selection }));
    }, []);

    const resetSelectionForScope = useCallback(
        (scopeId: string) => {
            setScopeGridSelections(
                /* istanbul ignore next -- defensive optional chain */
                (prev) => ({
                    ...prev,
                    [scopeId]: buildDefaultSelection(scopeGridDataMap[scopeId]?.gridData)
                })
            );
        },
        [scopeGridDataMap]
    );

    const resetAllSelections = useCallback(() => {
        setScopeGridSelections(buildDefaultSelectionsRef.current());
    }, []);

    /* istanbul ignore next -- defensive fallbacks for map iteration */
    const scopeGridInfos: ScopeGridInfo[] = useMemo(() => {
        return Object.keys(scopeGridDataMap).map((scopeId) => {
            const entry = scopeGridDataMap[scopeId];
            return {
                scopeId,
                hasGrid: entry?.hasGrid ?? false,
                offerType: entry?.offerType,
                isFlexBuy: entry?.isFlexBuy ?? false,
                gridData: entry?.gridData,
                selection: scopeGridSelections[scopeId] ?? buildDefaultSelection(entry?.gridData)
            };
        });
    }, [scopeGridDataMap, scopeGridSelections]);

    const getSelectionForPayload = useCallback(
        (scopeId: string): ScopeGridSelection | undefined => {
            if (!selectedScopeIds.includes(scopeId)) return undefined;
            return scopeGridSelections[scopeId];
        },
        [selectedScopeIds, scopeGridSelections]
    );

    return {
        scopeGridSelections,
        scopeGridInfos,
        toggleTermIndex,
        toggleDownPaymentIndex,
        setSelectionForScope,
        resetSelectionForScope,
        resetAllSelections,
        getSelectionForPayload
    };
};
